import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Plus, Phone, Mail, User } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const CATEGORIES = ["Shore Operations", "Vessel Emergency", "Medical / Telehealth", "Starlink / Comms", "Port Agent", "Flag State", "Class Society", "Client Contact", "Other"];

const EmployeeEmergencyContacts = () => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", phone: "", email: "", category: "Shore Operations", priority: "0", notes: "" });

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["emergency-contacts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("emergency_contacts").select("*").eq("is_active", true).order("priority", { ascending: true }).order("category");
      if (error) throw error;
      return data;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      const { error } = await supabase.from("emergency_contacts").insert({ ...values, priority: parseInt(values.priority) || 0 });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emergency-contacts"] });
      toast.success("Contact added");
      setForm({ name: "", role: "", phone: "", email: "", category: "Shore Operations", priority: "0", notes: "" });
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  // Group by category
  const grouped = contacts.reduce((acc: Record<string, any[]>, c: any) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {});

  return (
    <IntranetLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Emergency Contact Tree</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Who to call for what — shore operations, vessel emergencies, Starlink/telehealth</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => toast.success("Created: Add Contact", { description: "New entry added." })} size="sm" className="bg-teal hover:bg-teal-light text-white gap-2"><Plus className="w-4 h-4" /> Add Contact</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Emergency Contact</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); addMutation.mutate(form); }} className="space-y-4">
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <Input placeholder="Role / Title" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder="Priority (0 = highest)" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
              </div>
              <Textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              <Button type="submit" className="w-full bg-teal hover:bg-teal-light text-white" disabled={addMutation.isPending}>Add Contact</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" /></div>
      ) : contacts.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><AlertTriangle className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground font-body">No emergency contacts added yet</p></CardContent></Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, contacts]) => (
            <div key={category}>
              <h2 className="font-display text-base text-foreground mb-3 flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{category}</Badge>
                <span className="text-xs text-muted-foreground font-body">({(contacts as any[]).length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {(contacts as any[]).map((c) => (
                  <Card key={c.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-destructive" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-foreground font-body">{c.name}</h3>
                          <p className="text-xs text-muted-foreground font-body">{c.role}</p>
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-xs text-teal hover:underline font-body mt-1.5">
                              <Phone className="w-3 h-3" /> {c.phone}
                            </a>
                          )}
                          {c.email && (
                            <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-teal hover:underline font-body mt-0.5">
                              <Mail className="w-3 h-3" /> {c.email}
                            </a>
                          )}
                          {c.notes && <p className="text-[11px] text-muted-foreground/60 font-body mt-1.5">{c.notes}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </IntranetLayout>
  );
};

export default EmployeeEmergencyContacts;
